// src/components/Notes.jsx
import React, { useState, useEffect } from "react";
import { Trash2, Edit, Plus } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { fetchNotes, addNote, updateNote, deleteNote } from "../api/prepExtras";

export default function Notes({ materialId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newNote, setNewNote] = useState({ content: "", page: "", tag: "", color: "#FFD700" });
  const [editNoteData, setEditNoteData] = useState(null);

  useEffect(() => {
    loadNotes();
  }, [materialId]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await fetchNotes(materialId);
      setNotes(res.data || []);
    } catch (err) {
      console.error("Failed to fetch notes", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newNote.content.trim()) return alert("Note content required");
    try {
      const res = await addNote(materialId, newNote);
      setNotes((prev) => [...prev, res.data]);
      setNewNote({ content: "", page: "", tag: "", color: "#FFD700" });
      setShowAddModal(false);
    } catch (err) {
      console.error(err);
      alert("Failed to add note");
    }
  };

  const handleEditSave = async () => {
    if (!editNoteData.content.trim()) return alert("Note content required");
    try {
      await updateNote(materialId, editNoteData.id, editNoteData);
      setNotes((prev) => prev.map((n) => (n.id === editNoteData.id ? { ...n, ...editNoteData } : n)));
      setShowEditModal(false);
      setEditNoteData(null);
    } catch (err) {
      console.error(err);
      alert("Failed to update note");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteNote(materialId, id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete note");
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">{notes.length} note{notes.length !== 1 ? "s" : ""}</div>
        <button onClick={() => setShowAddModal(true)} className="px-3 py-1 bg-blue-600 text-white rounded text-sm flex items-center gap-2"><Plus size={14}/> Add</button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading notes...</div>
      ) : (
        <div className="space-y-3">
          {notes.map(n => (
            <div key={n.id} className="bg-white p-3 rounded shadow-sm">
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: n.content }} />
              <div className="text-xs text-gray-500 flex items-center gap-2 mt-2">
                <span>Tag: {n.tag || "-"}</span>
                <span>Page: {n.page || "-"}</span>
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: n.color || "#FFD700" }} />
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => { setEditNoteData({ ...n }); setShowEditModal(true); }} className="text-green-600"><Edit size={14} /></button>
                <button onClick={() => handleDelete(n.id)} className="text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-3">Add Note</h3>
            <ReactQuill value={newNote.content} onChange={(val) => setNewNote({ ...newNote, content: val })} />
            <div className="flex gap-2 items-center mt-3">
              <input type="number" placeholder="Page" value={newNote.page} onChange={(e) => setNewNote({ ...newNote, page: e.target.value })} className="border p-2 rounded w-24" />
              <input type="text" placeholder="Tag" value={newNote.tag} onChange={(e) => setNewNote({ ...newNote, tag: e.target.value })} className="border p-2 rounded w-40" />
              <input type="color" value={newNote.color} onChange={(e) => setNewNote({ ...newNote, color: e.target.value })} className="w-10 h-10" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAddModal(false)} className="px-3 py-1 border rounded">Cancel</button>
              <button onClick={handleAdd} className="px-3 py-1 bg-blue-600 text-white rounded">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showEditModal && editNoteData && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-3">Edit Note</h3>
            <ReactQuill value={editNoteData.content} onChange={(val) => setEditNoteData({ ...editNoteData, content: val })} />
            <div className="flex gap-2 items-center mt-3">
              <input type="number" placeholder="Page" value={editNoteData.page} onChange={(e) => setEditNoteData({ ...editNoteData, page: e.target.value })} className="border p-2 rounded w-24" />
              <input type="text" placeholder="Tag" value={editNoteData.tag} onChange={(e) => setEditNoteData({ ...editNoteData, tag: e.target.value })} className="border p-2 rounded w-40" />
              <input type="color" value={editNoteData.color || "#FFD700"} onChange={(e) => setEditNoteData({ ...editNoteData, color: e.target.value })} className="w-10 h-10" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setShowEditModal(false); setEditNoteData(null); }} className="px-3 py-1 border rounded">Cancel</button>
              <button onClick={handleEditSave} className="px-3 py-1 bg-green-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
